import { useEffect, useState } from 'react'

import { api } from '../../api/client'
import type { AccessImpostazioni, AccessProvaTSA } from '../../api/types'
import { t } from '../../i18n/lingua'
import { Card, ErrorBanner, Spinner, formatDateTime } from '../common'
import { DataTable } from '../DataTable'
import { SezioneSiem } from './Siem'

/**
 * «Impostazioni» of CENSO ACCESS: working hours (what «fuori orario» means),
 * retention (never below six months), the non-privileged users (off unless a
 * legal basis is declared), the extra privileged groups, the detection
 * thresholds and who gets them by mail. Every change goes in the register
 * itself, with who made it: the reviewer sees who changed the rules.
 */

const GIORNI: [string, () => string][] = [
  ['1', () => t('lunedì')], ['2', () => t('martedì')], ['3', () => t('mercoledì')], ['4', () => t('giovedì')],
  ['5', () => t('venerdì')], ['6', () => t('sabato')], ['7', () => t('domenica')],
]

const SOGLIE: [string, () => string][] = [
  ['brute_force_failures', () => t('Forza bruta: tentativi falliti')],
  ['brute_force_minutes', () => t('Forza bruta: in minuti')],
  ['spray_accounts', () => t('Password spray: account diversi')],
  ['spray_minutes', () => t('Password spray: in minuti')],
  ['success_after_failures', () => t('Successo dopo fallimenti: quanti fallimenti')],
  ['success_after_minutes', () => t('Successo dopo fallimenti: in minuti')],
  ['lateral_hosts', () => t('Movimento laterale: sistemi diversi')],
  ['lateral_minutes', () => t('Movimento laterale: in minuti')],
  ['dormant_days', () => t('Privilegiato dormiente: giorni senza accessi')],
  ['new_host_days', () => t('Primo accesso a un sistema: giorni di storia')],
  ['shared_sources', () => t('Account condiviso: postazioni diverse nello stesso momento')],
]

const PAESI: Record<string, () => string> = {
  IT: () => t('Italia'), FR: () => t('Francia'), DE: () => t('Germania'), ES: () => t('Spagna'), GB: () => t('Regno Unito'),
  CH: () => t('Svizzera'), AT: () => t('Austria'), NL: () => t('Paesi Bassi'), BE: () => t('Belgio'), PT: () => t('Portogallo'),
  IE: () => t('Irlanda'), US: () => t('Stati Uniti'),
}

const ORIGINI: Record<string, () => string> = {
  builtin: () => t('per difetto (dal SID)'),
  tenant: () => t('aggiunto qui'),
  local_admin: () => t('fra gli amministratori locali'),
  nested: () => t('annidato'),
}

const righe = (testo: string) => testo.split(/[\n,;]+/).map((x) => x.trim()).filter(Boolean)

export function VistaImpostazioni({ scrive }: { scrive: boolean }) {
  const [p, setP] = useState<AccessImpostazioni | null>(null)
  const [errore, setErrore] = useState<unknown>(null)
  const [salvato, setSalvato] = useState(false)
  const [busy, setBusy] = useState(false)
  const [orari, setOrari] = useState<Record<string, string>>({})
  const [festivi, setFestivi] = useState('')
  const [gruppi, setGruppi] = useState('')
  const [mail, setMail] = useState('')
  const [base, setBase] = useState('')
  const [ammessi, setAmmessi] = useState('')
  const [tsaPassword, setTsaPassword] = useState('')
  const [prova, setProva] = useState<AccessProvaTSA | null>(null)
  const [provo, setProvo] = useState(false)

  async function provaTSA() {
    setProvo(true)
    setProva(null)
    try {
      setProva(await api.accessTsaTest())
    } catch (err) {
      setErrore(err)
    } finally {
      setProvo(false)
    }
  }

  function carica(v: AccessImpostazioni) {
    setP(v)
    setOrari(Object.fromEntries(GIORNI.map(([g]) => [g, (v.working_hours[g] ?? []).join(', ')])))
    setFestivi(v.holidays.join('\n'))
    setGruppi(v.privileged_groups.join('\n'))
    setMail(v.notify_emails.join('\n'))
    setAmmessi((v.syslog_allowed ?? []).join('\n'))
  }
  useEffect(() => { api.accessSettings().then(carica).catch(setErrore) }, [])

  async function salva(e: React.FormEvent) {
    e.preventDefault()
    if (!p) return
    setBusy(true)
    setErrore(null)
    setSalvato(false)
    try {
      const corpo: Partial<AccessImpostazioni> & { standard_users_basis?: string } = {
        retention_days: p.retention_days, timezone: p.timezone, country: p.country,
        working_hours: Object.fromEntries(GIORNI.map(([g]) => [g, righe(orari[g] ?? '')])),
        holidays: righe(festivi), privileged_groups: righe(gruppi), notify_emails: righe(mail),
        detections: p.detections, standard_users: p.standard_users, sources: p.sources,
        tsa_url: p.tsa_url ?? '', tsa_username: p.tsa_username ?? '', archive_probe_id: p.archive_probe_id ?? '',
        archive_probe_secondary_id: p.archive_probe_secondary_id ?? '', archive_probe_dr_id: p.archive_probe_dr_id ?? '',
        syslog_port: p.syslog_port ?? 514, syslog_allowed: righe(ammessi), syslog_replica_mode: p.syslog_replica_mode ?? '',
      }
      if (tsaPassword) (corpo as Record<string, unknown>).tsa_password = tsaPassword
      if (p.standard_users && base.trim()) corpo.standard_users_basis = base.trim()
      carica(await api.accessSettingsSave(corpo))
      setBase('')
      setTsaPassword('')
      setSalvato(true)
    } catch (err) {
      setErrore(err)
    } finally {
      setBusy(false)
    }
  }

  if (!p) return errore ? <ErrorBanner error={errore} /> : <Spinner />
  return (
    <>
    <Card title={t('Impostazioni del registro')}>
      {!scrive && <p className="field__hint" style={{ marginTop: 0 }}>{t('Sola lettura: le impostazioni le cambia il Responsabile degli accessi.')}</p>}
      <form className="form-dialog" onSubmit={(e) => void salva(e)}>
        <fieldset disabled={!scrive || busy} style={{ border: 0, padding: 0, margin: 0 }}>
          <h4 className="form__sezione">{t('Orario di lavoro')}</h4>
          <p className="field__hint">{t('Fuori da queste fasce, nelle festività del Paese e nei giorni aggiunti, un accesso è «fuori orario». Le fasce si scrivono come 08:00-13:00, separate da virgola; un giorno vuoto è tutto fuori orario. I reperibili si segnano sulla scheda della persona.')}</p>
          <div className="form-grid">
            {GIORNI.map(([g, nome]) => (
              <label key={g} className="field span-2"><span className="field__label">{nome()}</span>
                <input type="text" value={orari[g] ?? ''} onChange={(e) => setOrari({ ...orari, [g]: e.target.value })} placeholder="08:00-13:00, 14:00-18:00" /></label>
            ))}
            <label className="field span-2"><span className="field__label">{t('Fuso orario')}</span>
              <input type="text" value={p.timezone} onChange={(e) => setP({ ...p, timezone: e.target.value })} /></label>
            <label className="field span-2"><span className="field__label">{t('Festività del Paese')}</span>
              <select value={p.country} onChange={(e) => setP({ ...p, country: e.target.value })}>
                {p.countries.map((c) => <option key={c} value={c}>{PAESI[c]?.() ?? c}</option>)}
              </select></label>
            <label className="field span-6"><span className="field__label">{t('Giorni aggiunti (AAAA-MM-GG, uno per riga; con «+» davanti un giorno lavorativo, es. +2026-12-24)')}</span>
              <textarea rows={3} value={festivi} onChange={(e) => setFestivi(e.target.value)} spellCheck={false} /></label>
          </div>

          <h4 className="form__sezione">{t('Conservazione')}</h4>
          <div className="form-grid">
            <label className="field span-2"><span className="field__label">{t('Giorni di conservazione (minimo {n})', { n: p.retention_min })}</span>
              <input type="number" min={p.retention_min} max={3650} value={p.retention_days} onChange={(e) => setP({ ...p, retention_days: Number(e.target.value) })} /></label>
            <p className="field__hint span-4">{t('Gli eventi più vecchi escono ogni notte, lasciando un’ancora nella catena di integrità. Sotto i sei mesi non si scende: è il minimo del Garante per gli amministratori di sistema.')}</p>
          </div>

          <h4 className="form__sezione">{t('Fonti')}</h4>
          <div className="form-grid">
            <label className="checkbox span-6"><input type="checkbox" checked={p.sources.agents !== false} onChange={(e) => setP({ ...p, sources: { ...p.sources, agents: e.target.checked } })} />{' '}{t('Gli agenti leggono gli accessi delle loro macchine (Windows, Linux, domain controller)')}</label>
            <label className="checkbox span-6"><input type="checkbox" checked={p.sources.syslog !== false} onChange={(e) => setP({ ...p, sources: { ...p.sources, syslog: e.target.checked } })} />{' '}{t('Le sonde con la funzione «Syslog per CENSO ACCESS» ricevono il syslog degli apparati')}</label>
            <label className="field span-2"><span className="field__label">{t('Porta syslog (UDP e TCP)')}</span>
              <input type="number" min={1} max={65535} value={p.syslog_port ?? 514} onChange={(e) => setP({ ...p, syslog_port: Number(e.target.value) })} /></label>
            <label className="field span-6"><span className="field__label">{t('Copia del syslog grezzo su altre sonde')}</span>
              <select value={p.syslog_replica_mode ?? ''} onChange={(e) => setP({ ...p, syslog_replica_mode: e.target.value as '' | 'censo' | 'dual' })}>
                <option value="">{t('nessuna: il grezzo resta sulla sonda che lo riceve')}</option>
                <option value="censo">{t('CENSO copia ogni segmento sulle sonde d’archivio (seconda copia, DR)')}</option>
                <option value="dual">{t('gli apparati mandano a più sonde; CENSO registra ogni messaggio una volta')}</option>
              </select></label>
            {p.syslog_replica_mode === 'censo' && <p className="field__hint span-6">{t('La sonda che riceve manda ogni segmento chiuso (un’ora di grezzo, compresso) al Control Plane, che lo tiene solo finché le sonde d’archivio diverse da lei non l’hanno conservato e verificato. Traffico in più verso il Control Plane: il grezzo compresso di ogni ora, una volta.')}</p>}
            {p.syslog_replica_mode === 'dual' && <p className="field__hint span-6">{t('Si configurano gli apparati per mandare il syslog a due o tre sonde con la funzione «Syslog per CENSO ACCESS»: ognuna conserva il suo grezzo, e il registro prende ogni messaggio una volta sola, riconoscendolo dall’impronta del testo originale. Nessun traffico in più verso il Control Plane.')}</p>}
            <label className="field span-4"><span className="field__label">{t('Apparati ammessi (indirizzi o reti, uno per riga; vuoto = tutti)')}</span>
              <textarea rows={2} value={ammessi} onChange={(e) => setAmmessi(e.target.value)} spellCheck={false} placeholder="10.0.0.0/16" /></label>
            <p className="field__hint span-6">{t('Con gli agenti aggiornati e questa voce accesa, ogni agente legge il registro Security (Windows) o journald e auth.log (Linux) e consegna gli accessi col suo keep-alive; un domain controller anche i tentativi Kerberos e NTLM falliti e i membri dei gruppi privilegiati di dominio. Una singola fonte si sospende dalla vista Fonti.')}</p>
          </div>

          <h4 className="form__sezione">{t('Utenti non privilegiati')}</h4>
          <p className="field__hint">{t('Spenti, il registro tiene gli accessi degli account privilegiati, le modifiche amministrative e i tentativi falliti di tutti; quelli degli account non privilegiati senza il nome. Accenderli significa registrare l’attività dei dipendenti: serve una base giuridica (in Italia l’accordo sindacale o l’autorizzazione dell’Ispettorato, art. 4 dello Statuto dei lavoratori), che resta scritta qui con chi l’ha dichiarata.')}</p>
          <div className="form-grid">
            <label className="checkbox span-6"><input type="checkbox" checked={p.standard_users} onChange={(e) => setP({ ...p, standard_users: e.target.checked })} />{' '}{t('Registra anche gli accessi degli utenti non privilegiati')}</label>
            {p.standard_users && !p.standard_users_basis && (
              <label className="field span-6"><span className="field__label">{t('Base giuridica (obbligatoria)')}</span>
                <textarea rows={2} value={base} onChange={(e) => setBase(e.target.value)} placeholder={t('es. Accordo sindacale del 12/09/2026, art. 4 L. 300/1970')} required /></label>
            )}
            {p.standard_users_basis && (
              <p className="field__hint span-6">{t('Dichiarata da {chi} il {quando}: «{base}»', { chi: p.standard_users_by ?? '—', quando: formatDateTime(p.standard_users_at), base: p.standard_users_basis })}</p>
            )}
          </div>

          <h4 className="form__sezione">{t('Gruppi e ruoli privilegiati')}</h4>
          <p className="field__hint">{t('CENSO li riconosce da solo: i gruppi privilegiati per difetto (riconosciuti dal SID, in ogni lingua), i gruppi di dominio che trova fra gli amministratori locali delle macchine, i gruppi annidati in un gruppo privilegiato e, su Linux, i gruppi che sudoers abilita a tutto. I loro membri li legge l’agente del domain controller. Qui sotto si aggiungono solo quelli che CENSO non può vedere: uno per riga, senza il dominio.')}</p>
          {(p.detected_groups ?? []).length > 0 && (
            <DataTable rows={p.detected_groups ?? []} rowKey={(g) => g.name} conteggio={{ nome: t('gruppi') }} paginazione={{ predefinita: 10 }} columns={[
              { key: 'name', header: t('Riconosciuti da CENSO'), value: (g) => g.name, render: (g) => <strong>{g.name}</strong> },
              { key: 'origin', header: t('Come'), filter: 'select', value: (g) => ORIGINI[g.origin]?.() ?? g.origin,
                render: (g) => <>{ORIGINI[g.origin]?.() ?? g.origin}{g.via && <span className="muted"> · {t('dentro {gruppo}', { gruppo: g.via })}</span>}</> },
              { key: 'members', header: t('Membri'), type: 'number', value: (g) => g.members ?? -1,
                render: (g) => g.members === null ? <span className="muted" title={t('Nessun domain controller l’ha ancora descritto: serve l’agente aggiornato su un domain controller.')}>{t('da leggere')}</span> : g.members },
              { key: 'systems', header: t('Amministra'), type: 'number', value: (g) => g.systems, render: (g) => g.systems ? t('{n} sistemi', { n: g.systems }) : <span className="muted">—</span> },
            ]} />
          )}
          <div className="form-grid">
            <label className="field span-6"><span className="field__label">{t('Gruppi aggiunti')}</span>
              <textarea rows={3} value={gruppi} onChange={(e) => setGruppi(e.target.value)} spellCheck={false} placeholder={t('es. GG-Server-Admin')} /></label>
          </div>

          <h4 className="form__sezione">{t('Marca temporale')}</h4>
          <p className="field__hint">{t('Facoltativa. Una volta al giorno il sigillo del registro riceve una marca temporale RFC 3161 dall’autorità indicata qui: una data certa data da terzi. Per l’opponibilità in Italia serve una TSA qualificata eIDAS (Aruba, InfoCert, Namirial…), con le sue credenziali; per provare basta un servizio pubblico come https://freetsa.org/tsr. La chiamata parte dal Control Plane.')}</p>
          <div className="form-grid">
            <label className="field span-4"><span className="field__label">{t('Indirizzo della TSA')}</span>
              <input type="url" value={p.tsa_url ?? ''} onChange={(e) => setP({ ...p, tsa_url: e.target.value })} placeholder="https://freetsa.org/tsr" /></label>
            <label className="field span-2"><span className="field__label">{t('Utente (se la TSA lo chiede)')}</span>
              <input type="text" value={p.tsa_username ?? ''} onChange={(e) => setP({ ...p, tsa_username: e.target.value })} autoComplete="off" /></label>
            <label className="field span-2"><span className="field__label">{p.tsa_password_set ? t('Password (impostata: scrivi per cambiarla)') : t('Password')}</span>
              <input type="password" value={tsaPassword} onChange={(e) => setTsaPassword(e.target.value)} autoComplete="new-password" /></label>
            <div className="span-4" style={{ alignSelf: 'end' }}>
              <button type="button" className="secondary" onClick={() => void provaTSA()} disabled={provo || !p.tsa_url}>{provo ? t('Provo…') : t('Prova la TSA (impostazioni salvate)')}</button>
            </div>
            {prova && (
              <p className={`field__hint span-6 ${prova.ok ? '' : 'eagle-motivo'}`}>
                {prova.ok
                  ? t('Marca ottenuta: {quando}, numero {serie}, firmata da {chi}{verificata}', {
                    quando: formatDateTime(prova.time), serie: prova.serial ?? '—', chi: prova.signer ?? '—',
                    verificata: prova.signature_verified ? '' : t(' (firma da verificare con openssl)'),
                  }) + (prova.note ? ` · ${prova.note}` : '')
                  : prova.error}
              </p>
            )}
          </div>

          <h4 className="form__sezione">{t('Copia d’archivio')}</h4>
          <p className="field__hint">{t('Una sonda del tenant tiene una copia del registro: ogni giorno riceve la parte nuova firmata con la chiave del tenant, la controlla, la conserva in sola lettura (immutabile dove il file system lo permette) fino alla fine della conservazione e la rilegge. È un testimone che il database del Control Plane non controlla. Meglio una sonda dedicata, in un luogo diverso dal Control Plane.')}</p>
          <p className="field__hint">{t('Per la continuità si aggiungono una seconda copia e una terza per il disaster recovery, su sonde diverse e meglio in sedi diverse: ognuna tiene tutto il registro per conto suo, lo rilegge e ne risponde. Una sonda aggiunta dopo riceve tutto ciò che il registro conserva ancora.')}</p>
          <div className="form-grid">
            {([
              ['archive_probe_id', t('Sonda d’archivio')],
              ['archive_probe_secondary_id', t('Seconda copia')],
              ['archive_probe_dr_id', t('Terza copia (disaster recovery)')],
            ] as const).map(([campo, etichetta]) => (
              <label key={campo} className="field span-2"><span className="field__label">{etichetta}</span>
                <select value={p[campo] ?? ''} onChange={(e) => setP({ ...p, [campo]: e.target.value || null })}>
                  <option value="">{t('nessuna')}</option>
                  {(p.probes ?? []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select></label>
            ))}
          </div>

          <h4 className="form__sezione">{t('Rilevamenti')}</h4>
          <div className="form-grid">
            {SOGLIE.map(([k, nome]) => (
              <label key={k} className="field span-2"><span className="field__label">{nome()}</span>
                <input type="number" min={1} value={p.detections[k] ?? ''} onChange={(e) => setP({ ...p, detections: { ...p.detections, [k]: Number(e.target.value) } })} /></label>
            ))}
            <label className="field span-6"><span className="field__label">{t('Chi riceve i rilevamenti per posta (un indirizzo per riga)')}</span>
              <textarea rows={2} value={mail} onChange={(e) => setMail(e.target.value)} spellCheck={false} /></label>
          </div>
        </fieldset>
        <ErrorBanner error={errore} />
        {scrive && (
          <div className="form-dialog__azioni">
            <button type="submit" disabled={busy}>{busy ? t('Salvo…') : t('Salva')}</button>
            {salvato && <span className="muted">{t('Salvato: la modifica è nel registro.')}</span>}
          </div>
        )}
        {p.updated_by && <p className="field__hint">{t('Ultima modifica di {chi} il {quando}', { chi: p.updated_by, quando: formatDateTime(p.updated_at) })}</p>}
      </form>
    </Card>
    <SezioneSiem scrive={scrive} sonde={p.probes ?? []} />
    </>
  )
}
